import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const EMPTY = { name: "", title: "Dr.", contact: "", email: "", address: "", specialization: "", active: true };

export default function LabReferrers() {
  const { can } = useAuth();
  const manage = can("lab.referrers.manage");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const params = { q: q || undefined, letter: letter || undefined, active: activeOnly ? true : undefined };
      const { data } = await api.get("/lab/referrers", { params });
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [q, letter, activeOnly]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setFormError(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Doctor name is required."); return; }
    setBusy(true); setFormError("");
    try {
      if (editing) await api.patch(`/lab/referrers/${editing.referrer_id}`, form);
      else await api.post("/lab/referrers", form);
      toast.success(editing ? "Referral doctor updated." : "Referral doctor added.");
      setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = async (r) => { try { await api.patch(`/lab/referrers/${r.referrer_id}`, { active: !r.active }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!can("lab.orders.view")) return <NoAccess what="Referral doctors" />;

  return (
    <div data-testid="lab-referrers-page">
      <PageHeader overline="Cases" title="Referral Doctors"
        subtitle="Doctors and centres that refer patients to your lab. Archive keeps their historical referral business intact."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-referrer-btn"><Plus className="h-4 w-4" /> Add referrer</button>} />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / contact…" className={`${inputCls} max-w-xs`} data-testid="referrer-search" />
        <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="referrer-active-filter" /> Active only</label>
      </div>
      <div className="flex flex-wrap gap-1 mb-5" data-testid="referrer-alpha">
        <button onClick={() => setLetter("")} className={`px-2 py-1 rounded text-xs ${!letter ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>All</button>
        {LETTERS.map((l) => <button key={l} onClick={() => setLetter(l)} className={`px-2 py-1 rounded text-xs ${letter === l ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`referrer-letter-${l}`}>{l}</button>)}
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-referrers-table" columns={["Referrer", "Contact", "Specialization", "Address", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No referrers" subtitle="Add referring doctors to attribute lab business." testid="lab-referrers-empty" />}
          renderRow={(r) => (
            <tr key={r.referrer_id} data-testid={`lab-referrer-row-${r.referrer_id}`}>
              <Td className="font-medium text-slate-900">{r.title} {r.name}</Td>
              <Td className="text-slate-600">{r.contact || "—"}<span className="block text-xs text-slate-400">{r.email}</span></Td>
              <Td className="text-slate-600">{r.specialization || "—"}</Td>
              <Td className="text-slate-600">{r.address || "—"}</Td>
              <Td><span className={`text-xs font-medium ${r.active ? "text-emerald-600" : "text-slate-400"}`}>{r.active ? "Active" : "Inactive"}</span></Td>
              {manage && <Td>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(r)} className={ghostBtnCls} data-testid={`edit-referrer-${r.referrer_id}`}>Edit</button>
                  <button onClick={() => toggle(r)} className={ghostBtnCls} data-testid={`toggle-referrer-${r.referrer_id}`}>{r.active ? "Archive" : "Restore"}</button>
                </div>
              </Td>}
            </tr>
          )} />
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit referrer" : "Add referrer"} wide testid="lab-referrer-modal">
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Title"><select className={inputCls} data-testid="referrer-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}><option>Dr.</option><option>Prof.</option><option>Mr.</option><option>Ms.</option><option>Centre</option></select></Field>
            <Field label="Name *"><input className={inputCls} data-testid="referrer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Contact number"><input className={inputCls} data-testid="referrer-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></Field>
            <Field label="Email"><input className={inputCls} data-testid="referrer-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Specialization"><input className={inputCls} data-testid="referrer-spec" value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} /></Field>
            <Field label="Address"><input className={inputCls} data-testid="referrer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="referrer-active" /> Active</label>
          {formError && <p className="text-sm text-red-600" data-testid="referrer-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-referrer-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
